<script setup lang="ts">
import {
  HoverCardArrow,
  HoverCardContent,
  HoverCardPortal,
  HoverCardRoot,
  HoverCardTrigger,
} from 'reka-ui'

type TagTheme = 'light' | 'dark'
type TagSize = 'xsmall' | 'small' | 'medium'
type TagVariant = 'filled' | 'outline'
type TagStyle = 'default' | 'faded' | 'intense' | 'outline'
type TagType =
    | 'primary'
    | 'secondary'
    | 'light-blue'
    | 'teal'
    | 'pink'
    | 'purple'
    | 'dark-blue'
    | 'sand'
    | 'brown'
    | 'bleu-clair'
    | 'turquoise'
    | 'orange'
    | 'rose'
    | 'violet'
    | 'bleu-fonce'
    | 'sable'
    | 'marron'
    | 'success'
    | 'warning'
    | 'error'
    | 'neutral'
    | 'default'
    | 'defaut'
    | 'enedis-blue'

interface Props {
  text: string
  theme?: TagTheme
  type?: TagType
  variant?: TagVariant
  /** Style DS : default, faded, intense, outline */
  tagStyle?: TagStyle
  size?: TagSize
  icon?: string
  iconPosition?: 'left' | 'right'
  hoverText?: string
  /** Sans halo / ombre au survol (ex. pastille « Mode édition » du header). */
  flat?: boolean
}

withDefaults(defineProps<Props>(), {
  theme: 'light',
  type: 'defaut',
  variant: 'filled',
  tagStyle: 'default',
  size: 'small',
  icon: undefined,
  iconPosition: 'left',
  hoverText: undefined,
  flat: false,
})
</script>

<template>
  <ClientOnly>
    <HoverCardRoot v-if="hoverText" :open-delay="400">
      <HoverCardTrigger as-child>
        <div
          class="source-tag"
          :class="[type, size, theme, tagStyle !== 'default' ? tagStyle : null, { outline: variant === 'outline' || tagStyle === 'outline', 'source-tag--flat': flat }]"
        >
          <span
            v-if="icon && iconPosition === 'left'"
            class="material-symbols-rounded source-tag__icon"
            aria-hidden="true"
          >{{ icon }}</span>
          <span class="source-tag__label">{{ text }}</span>
          <span
            v-if="icon && iconPosition === 'right'"
            class="material-symbols-rounded source-tag__icon"
            aria-hidden="true"
          >{{ icon }}</span>
        </div>
      </HoverCardTrigger>
      <HoverCardPortal>
        <HoverCardContent class="source-tag__hover-card" :side-offset="8">
          <HoverCardArrow class="source-tag__hover-arrow" />
          <div class="source-tag__hover-body">
            <div class="source-tag__hover-title">
              <div class="source-tag__hover-lib">{{ text }}</div>
              <div class="source-tag__hover-lib-long">{{ hoverText }}</div>
            </div>
          </div>
        </HoverCardContent>
      </HoverCardPortal>
    </HoverCardRoot>

    <div
      v-else
      class="source-tag"
      :class="[type, size, theme, tagStyle !== 'default' ? tagStyle : null, { outline: variant === 'outline' || tagStyle === 'outline', 'source-tag--flat': flat }]"
    >
      <span
        v-if="icon && iconPosition === 'left'"
        class="material-symbols-rounded source-tag__icon"
        aria-hidden="true"
      >{{ icon }}</span>
      <span class="source-tag__label">{{ text }}</span>
      <span
        v-if="icon && iconPosition === 'right'"
        class="material-symbols-rounded source-tag__icon"
        aria-hidden="true"
      >{{ icon }}</span>
    </div>

    <template #fallback>
      <div
        class="source-tag"
        :class="[type, size, theme, tagStyle !== 'default' ? tagStyle : null, { outline: variant === 'outline' || tagStyle === 'outline', 'source-tag--flat': flat }]"
      >
        <span
          v-if="icon && iconPosition === 'left'"
          class="material-symbols-rounded source-tag__icon"
          aria-hidden="true"
        >{{ icon }}</span>
        <span class="source-tag__label">{{ text }}</span>
        <span
          v-if="icon && iconPosition === 'right'"
          class="material-symbols-rounded source-tag__icon"
          aria-hidden="true"
        >{{ icon }}</span>
      </div>
    </template>
  </ClientOnly>
</template>

<style scoped lang="scss">
@use "sass:map";
@use "~/assets/scss/partials/elpdl/colors" as c;
@use "~/assets/scss/partials/elpdl/spacing" as *;
@use "~/assets/scss/partials/elpdl/z-index" as z;
@use "~/assets/scss/app" as *;

$tag-faded-bg: (
  "primary": c.$enedis-primary-200,
  "enedis-blue": c.$enedis-primary-200,
  "secondary": c.$enedis-secondary-200,
  "success": c.$enedis-success-200,
  "warning": c.$enedis-warning-200,
  "error": c.$enedis-error-200,
  "neutral": c.$enedis-neutral-30,
  "orange": c.$enedis-interface-orange-200,
  "pink": c.$enedis-interface-prune-200,
  "rose": c.$enedis-interface-prune-200,
  "purple": c.$enedis-interface-violet-200,
  "violet": c.$enedis-interface-violet-200,
  "dark-blue": c.$enedis-interface-bleufonce-200,
  "bleu-fonce": c.$enedis-interface-bleufonce-200,
  "sand": c.$enedis-interface-taupe-200,
  "sable": c.$enedis-interface-taupe-200,
  "brown": c.$enedis-interface-marron-200,
  "marron": c.$enedis-interface-marron-200,
  "bleu-clair": c.$enedis-interface-bleumoyen-200,
  "turquoise": c.$enedis-interface-turquoise-200,
);

$tag-faded-fg: (
  "primary": c.$enedis-primary-700,
  "enedis-blue": c.$enedis-primary-700,
  "secondary": c.$enedis-secondary-700,
  "success": c.$enedis-success-700,
  "warning": c.$enedis-warning-700,
  "error": c.$enedis-error-700,
  "neutral": c.$enedis-neutral-700,
  "orange": c.$enedis-interface-orange-700,
  "pink": c.$enedis-interface-prune-700,
  "rose": c.$enedis-interface-prune-700,
  "purple": c.$enedis-interface-violet-700,
  "violet": c.$enedis-interface-violet-700,
  "dark-blue": c.$enedis-interface-bleufonce-700,
  "bleu-fonce": c.$enedis-interface-bleufonce-700,
  "sand": c.$enedis-interface-taupe-700,
  "sable": c.$enedis-interface-taupe-700,
  "brown": c.$enedis-interface-marron-700,
  "marron": c.$enedis-interface-marron-700,
  "bleu-clair": c.$enedis-interface-bleumoyen-700,
  "turquoise": c.$enedis-interface-turquoise-700,
);

$tag-intense-bg: (
  "primary": c.$enedis-primary-500,
  "enedis-blue": c.$enedis-primary-500,
  "secondary": c.$enedis-secondary-700,
  "success": c.$enedis-success-700,
  "warning": c.$enedis-warning-700,
  "error": c.$enedis-error-500,
  "neutral": c.$enedis-neutral-500,
  "orange": c.$enedis-interface-orange-700,
  "pink": c.$enedis-interface-prune-500,
  "rose": c.$enedis-interface-prune-500,
  "purple": c.$enedis-interface-violet-500,
  "violet": c.$enedis-interface-violet-500,
  "dark-blue": c.$enedis-interface-bleufonce-500,
  "bleu-fonce": c.$enedis-interface-bleufonce-500,
  "sand": c.$enedis-interface-taupe-700,
  "sable": c.$enedis-interface-taupe-700,
  "brown": c.$enedis-interface-marron-500,
  "marron": c.$enedis-interface-marron-500,
  "bleu-clair": c.$enedis-interface-bleumoyen-600,
  "turquoise": c.$enedis-interface-turquoise-700,
);

/* base - Source UI Kit : gap 4px, radius 4px, Public Sans Medium */
.source-tag {
  display: inline-flex;
  flex-flow: row nowrap;
  align-items: center;
  justify-content: center;
  gap: $spacing-3xs;
  border-radius: 4px;
  border: none;
  width: fit-content;
  max-width: 100%;
  box-sizing: border-box;
  transition:
    background-color 120ms ease,
    border-color 120ms ease,
    box-shadow 120ms ease;
}

.source-tag__label {
  margin: 0;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.source-tag:hover:not(.outline):not(.source-tag--flat) {
  box-shadow: inset 0 0 0 9999px rgba(16, 16, 20, 0.05);
}

.source-tag.outline {
  border: 1px solid c.$enedis-neutral-60;
}

.source-tag.outline.default,
.source-tag.outline.defaut {
  border-color: c.$enedis-primary-500;
}

.source-tag.outline:hover:not(.source-tag--flat) {
  border-color: c.$enedis-neutral-80;
  box-shadow: 0 0 0 3px rgba(16, 16, 20, 0.16);
}

.source-tag.outline.default:hover:not(.source-tag--flat),
.source-tag.outline.defaut:hover:not(.source-tag--flat) {
  border-color: c.$enedis-primary-600;
  box-shadow: 0 0 0 3px rgba(16, 16, 20, 0.16);
}

.source-tag.source-tag--flat.outline:hover {
  border-color: c.$enedis-neutral-80;
  box-shadow: none;
}

.source-tag.source-tag--flat.outline.default:hover,
.source-tag.source-tag--flat.outline.defaut:hover {
  border-color: c.$enedis-primary-600;
}

.source-tag__icon {
  flex: 0 0 auto;
  font-size: 1em;
  line-height: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-variation-settings: "FILL" 0, "wght" 400, "GRAD" 0, "opsz" 20;
}

/* sizes - Source UI Kit XSmall 2×4 / 12px icon / 14px ; Small 4×8 / 16px ; Medium 8×12 / 20px */
.source-tag.xsmall {
  padding: 2px $spacing-3xs;
  min-height: 0;

  .source-tag__label {
    font-size: 14px;
    line-height: 16px;
  }

  .source-tag__icon {
    font-size: 12px;
    width: 12px;
    height: 12px;
    font-variation-settings: "FILL" 0, "wght" 400, "GRAD" 0, "opsz" 12;
  }
}

.source-tag.small {
  padding: $spacing-3xs $spacing-2xs;

  .source-tag__label {
    font-size: 14px;
    line-height: 16px;
  }

  .source-tag__icon {
    font-size: 16px;
    width: 16px;
    height: 16px;
    font-variation-settings: "FILL" 0, "wght" 400, "GRAD" 0, "opsz" 16;
  }
}

.source-tag.medium {
  padding: $spacing-2xs $spacing-xs;

  .source-tag__label {
    font-size: 16px;
    line-height: 20px;
  }

  .source-tag__icon {
    font-size: 20px;
    width: 20px;
    height: 20px;
    font-variation-settings: "FILL" 0, "wght" 400, "GRAD" 0, "opsz" 20;
  }
}

/* color text (global) */
.source-tag.primary,
.source-tag.secondary,
.source-tag.orange,
.source-tag.pink,
.source-tag.purple,
.source-tag.dark-blue,
.source-tag.sand,
.source-tag.brown,
.source-tag.bleu-clair,
.source-tag.turquoise,
.source-tag.rose,
.source-tag.violet,
.source-tag.bleu-fonce,
.source-tag.sable,
.source-tag.marron,
.source-tag.success,
.source-tag.warning,
.source-tag.error,
.source-tag.neutral {
  color: var(--color-text);
}

/* types */
.source-tag.primary { background-color: c.$enedis-primary-300; }
.source-tag.secondary { background-color: c.$enedis-secondary-300; }

/* theme enedis blue - fond rempli sans contour (maquette AgentCard / DS faded) */
.source-tag.enedis-blue {
  color: c.$enedis-primary-700;
  background-color: c.$enedis-primary-300;
  border: none;
}

.source-tag.enedis-blue:hover:not(.outline):not(.source-tag--flat) {
  border: none;
}

/* Développeur / manageur applicatif (pastilles profil) : bleu Enedis + contour bleu */
.source-tag.light-blue,
.source-tag.teal {
  color: c.$enedis-primary-700;
  background-color: c.$enedis-primary-300;
  border: 1px solid c.$enedis-primary-500;
}

.source-tag.light-blue:hover:not(.outline):not(.source-tag--flat),
.source-tag.teal:hover:not(.outline):not(.source-tag--flat) {
  border-color: c.$enedis-primary-600;
}

/* aliases (french values returned by API) */
.source-tag.bleu-clair { background-color: c.$enedis-interface-bleumoyen-300; }
.source-tag.turquoise { background-color: c.$enedis-interface-turquoise-300; }
.source-tag.rose { background-color: c.$enedis-interface-prune-300; }
.source-tag.violet { background-color: c.$enedis-interface-violet-300; }
.source-tag.bleu-fonce { background-color: c.$enedis-interface-bleufonce-300; }
.source-tag.sable { background-color: c.$enedis-interface-taupe-300; }
.source-tag.marron { background-color: c.$enedis-interface-marron-300; }

.source-tag.success { background-color: c.$enedis-success-300; }
.source-tag.warning { background-color: c.$enedis-warning-300; }
.source-tag.error { background-color: c.$enedis-error-300; }
.source-tag.neutral { background-color: c.$enedis-interface-bleufonce-200; }

.source-tag.default {
  color: c.$enedis-primary;
  background-color: transparent;
  border: 1px solid c.$enedis-primary-500;
}

.source-tag.default:hover {
  background-color: var(--color-bg-elevated);
  border-color: c.$enedis-primary-600;
}

.source-tag.defaut {
  color: c.$enedis-primary;
  background-color: var(--color-bg-elevated);
  border: 1px solid c.$enedis-primary-500;
}

.source-tag.defaut:hover {
  background-color: var(--color-bg-elevated);
  border-color: c.$enedis-primary-600;
}

/* Style faded - fond 200 + texte 700 (Source UI Kit) */
@each $name, $bg in $tag-faded-bg {
  .source-tag.#{$name}.faded {
    background-color: $bg;
    color: map.get($tag-faded-fg, $name);
    border: none;
  }
}

/* Style intense - fond saturé + texte blanc (Source UI Kit) */
@each $name, $bg in $tag-intense-bg {
  .source-tag.#{$name}.intense {
    background-color: $bg;
    color: c.$enedis-neutral-10;
    border: none;
  }
}

/* Verso AgentCard : faded enedis-blue sur fond coloré → surface élevée */
.source-tag.enedis-blue.faded {
  color: c.$enedis-primary-700;
  background-color: var(--color-bg-elevated);
  border: none;
}

.source-tag.enedis-blue.faded .source-tag__icon {
  color: c.$enedis-primary-500;
}

:global(.source-tag__hover-card) {
  z-index: z.$z-popover;
  max-width: 320px;
  min-width: 200px;
  padding: $spacing-s $spacing-m;
  border-radius: 12px;
  background: var(--color-bg-elevated) !important;
  box-shadow: var(--color-shadow-card);
  border: 1px solid var(--color-border-subtle);
  position: relative;
}

:global(.source-tag__hover-card .source-tag__hover-arrow) {
  fill: var(--color-bg-elevated) !important;
  stroke: var(--color-border-subtle);
  stroke-width: 1px;
}

:global(.source-tag__hover-body) {
  color: var(--color-text-secondary);
  font-size: 0.875rem;
  font-weight: 400;
  line-height: 1.45;
  text-align: left;
}

:global(.source-tag__hover-title) {
  display: flex;
  flex-direction: column;
  gap: $spacing-3xs;
}

:global(.source-tag__hover-lib) {
  font-weight: 700;
  font-size: 0.9375rem;
  color: var(--color-text);
  line-height: 1.3;
}

:global(.source-tag__hover-lib-long) {
  font-size: 0.8125rem;
  font-weight: 500;
  color: var(--color-text-secondary);
  line-height: 1.35;
}
</style>
